import { useApi } from '@/@diffrag/hooks/useApi'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentView } from '@/@diffrag/layout/ContentView'
import { SimpleTable } from '@/@diffrag/table/SimpleTable'
import { api } from '@/api'
import { IAdminClinicInterviewRO, StatusListEnum } from 'api'
import { useState, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'
import dayjs from 'dayjs'
import { OffsetPagination } from '@/@diffrag/OffsetPagination'
import { Pagination } from '@/components/ui/pagination'
import { useNavigate } from 'react-router-dom'

export default function DoctorInterview() {
  const page = useQueryValue('page', 1)
  const q = useQueryValue('q', undefined)
  const navigate = useNavigate()

  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])

  const statusOptions = [
    { value: 'PENDING_GROUP', label: '대기' },
    { value: StatusListEnum.APPROVED, label: '승인' },
    { value: StatusListEnum.REJECTED, label: '거절' },
  ]

  // 백엔드로 보낼 실제 status 값들로 변환
  const actualStatusList = useMemo(() => {
    const result: StatusListEnum[] = []
    selectedStatuses.forEach((status) => {
      if (status === 'PENDING_GROUP') {
        result.push(StatusListEnum.PENDING, StatusListEnum.PENDING_UPDATE)
      } else {
        result.push(status as StatusListEnum)
      }
    })
    return result
  }, [selectedStatuses])

  const { data, isFetching, refetch } = useApi({
    id: 'userList',
    fn: api().adminClinicInterview.adminClinicInterviewList,
    params: {
      page,
      q,
      statusList: actualStatusList,
    },
  })

  const selectedStatusLabels = useMemo(() => {
    if (selectedStatuses.length === 0) {
      return '전체'
    }

    const labels = statusOptions
      .filter((option) => selectedStatuses.includes(option.value))
      .map((option) => option.label)

    return labels.join(', ')
  }, [selectedStatuses])

  const handleStatusToggle = (status: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    )
  }

  const onDisable = async (rows: IAdminClinicInterviewRO[]) => {
    await api().adminClinicInterview.doctorProfileRejectMultipleCreate({
      ids: rows.map((row) => row.id),
    })

    refetch()
  }

  return (
    <ContentView>
      <div className="mb-4 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">상태 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[200px] justify-between"
              >
                {selectedStatusLabels}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[200px]">
              {statusOptions.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value}
                  checked={selectedStatuses.includes(option.value)}
                  onCheckedChange={() => handleStatusToggle(option.value)}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <SimpleTable
        options={{
          disableNew: true,
          disableSort: true,
        }}
        onRowClick={(row) => {
          navigate(`/approval-management/doctor-interview/edit?id=${row.id}`)
        }}
        deleteText="거절"
        onDelete={onDisable}
        totalCount={data?.count}
        columns={[
          {
            label: 'NO',
            key: 'id',
            width: 100,
            isPrimary: true,
          },
          {
            label: '병원명',
            key: 'clinicName',
            width: 150,
            isPrimary: true,
          },
          {
            label: '원장님성함',
            key: 'doctorName',
            width: 120,
            isPrimary: true,
          },
          {
            label: '등록일',
            key: 'createDate',
            width: 120,
            formatter: (value) =>
              value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
          },
          {
            label: '상태',
            key: 'status',
            width: 100,
            valueMap: {
              PENDING: '대기',
              PENDING_UPDATE: '대기(수정 요청)',
              APPROVED: '승인',
              REJECTED: '거절',
            },
          },
          {
            label: '승인일',
            key: 'approveDate',
            width: 120,
            formatter: (value) =>
              value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
          },
          {
            label: '거절일',
            key: 'rejectDate',
            width: 120,
            formatter: (value) =>
              value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
          },
        ]}
        data={data?.rows}
        isLoading={isFetching}
      />
      <OffsetPagination page={page} total={data?.count} pageSize={10} />
    </ContentView>
  )
}
